import React, { useState, useEffect } from 'react'
import { makeStyles, Tooltip, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import type { CalendarEvent } from 'paintscout'
import { getCrews } from '@paintscout/util/builder'
import { CREW_COLORS } from '@paintscout/util'
import LockIcon from '@material-ui/icons/Lock'
import ProductionEventPopper from '../ProductionEventPopper'
import type { CrewPalette } from '../util'
import { getCrewPalette } from '../util'
import { useProductionEventPermissions } from 'shared/hooks'

interface MonthEventStyleProps extends CrewPalette {
  isHovered: boolean
}

const useStyles = makeStyles<Theme, MonthEventStyleProps>((theme) => ({
  root: {
    position: 'relative',
    backgroundColor: ({ surface, surfaceHover, isHovered }) => (isHovered ? surfaceHover : surface),
    borderRadius: theme.borderRadius.md,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(0.75),
    boxSizing: 'border-box',
    height: '25px',
    padding: `0 ${theme.spacing(0.75)}px 0 ${theme.spacing(1.5)}px`,
    transition: 'background-color 0.1s ease-in-out',
    [theme.breakpoints.down('xs')]: {
      height: '37px',
      padding: `0 ${theme.spacing(1.25)}px 0 ${theme.spacing(2)}px`
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      left: theme.spacing(0.5),
      top: '50%',
      transform: 'translateY(-50%)',
      width: '4px',
      height: '14px',
      borderRadius: '5px',
      backgroundColor: ({ accent }) => accent,
      [theme.breakpoints.down('xs')]: {
        width: '4px',
        height: '20px',
        left: theme.spacing(0.75)
      }
    },
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: ({ surfaceHover }) => surfaceHover
      }
    }
  },
  label: {
    color: ({ accent }) => accent,
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.4,
    margin: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(14),
      flex: 1
    }
  },
  viewOnlyOverlay: {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1,
      borderRadius: 'inherit',
      backgroundImage: `
        repeating-linear-gradient(
          135deg,
          rgba(0,0,0,0.04),
          rgba(0,0,0,0.04) 10px,
          transparent 10px,
          transparent 13px
        )
      `
    }
  },
  lockIcon: {
    width: 12,
    height: 12,
    color: ({ accent }) => accent
  },
  crewBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    paddingTop: '2px',
    paddingBottom: '2px',
    marginBottom: '4px',
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75),
    borderRadius: theme.borderRadius.md,
    backgroundColor: ({ accentSoft }) => accentSoft,
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(10),
    fontWeight: 400,
    lineHeight: 1.4,
    flexShrink: 0,
    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(12),
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1)
    }
  }
}))

interface MonthEventProps {
  event: CalendarEvent
}

export function MonthEvent(props: MonthEventProps) {
  const { event } = props
  const [isHovered, setIsHovered] = useState(false)
  const { options } = useClientOptions()
  const crewOptions = getCrews({ options, includeTrashed: true })
  const eventCrew = event?.crew ? crewOptions.find((crew) => crew._id === event.crew) : null
  const palette = getCrewPalette(eventCrew?.color || CREW_COLORS[0][0])
  const classes = useStyles({ ...palette, isHovered })
  const crewLabel = eventCrew?.name || 'Unassigned'

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { permissionToViewDetails } = useProductionEventPermissions(event)
  const isViewOnly = !permissionToViewDetails

  // Create a BroadcastChannel
  const hoverChannel = new BroadcastChannel('hover-events')

  const handleMouseEnter = () => {
    hoverChannel.postMessage({ eventId: event.id }) // Broadcast the hover event
  }

  const handleMouseLeave = () => {
    hoverChannel.postMessage({ eventId: null }) // Broadcast that no event is hovered
  }

  useEffect(() => {
    // Listen for hover events
    const handleHoverEvent = (message: MessageEvent) => {
      if (message.data.eventId === event.id) {
        setIsHovered(true)
      } else {
        setIsHovered(false)
      }
    }

    hoverChannel.addEventListener('message', handleHoverEvent)

    return () => {
      hoverChannel.removeEventListener('message', handleHoverEvent)
      hoverChannel.close() // Clean up the channel
    }
  }, [event.id, hoverChannel])

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : e.currentTarget)
  }

  return (
    <>
      <div
        className={`${classes.root}${isViewOnly ? ` ${classes.viewOnlyOverlay}` : ''}`}
        style={{ cursor: isViewOnly ? 'default' : undefined }}
        onClick={isViewOnly ? undefined : handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Typography className={classes.label}>{event?.deal?.name}</Typography>
        {isViewOnly && (
          <Tooltip content={`You don't have permission to access this job.`} hideIcon>
            <LockIcon className={classes.lockIcon} />
          </Tooltip>
        )}
        {eventCrew && (
          <Tooltip content={eventCrew?.name || null} hideIcon>
            <span className={classes.crewBadge}>{crewLabel}</span>
          </Tooltip>
        )}
      </div>
      <ProductionEventPopper event={event} onClose={() => setAnchorEl(null)} anchorEl={anchorEl} />
    </>
  )
}
